import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Icon,
  Collapse,
  Button,
  Progress,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Lightbulb,
  RefreshCw,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { userPreferencesApi, PromptValidation } from '../../../apis/user-preferences';

interface PromptValidatorProps {
  description: string;
  isVisible: boolean;
  onValidationChange?: (validation: PromptValidation | null) => void;
}

export const PromptValidator: React.FC<PromptValidatorProps> = ({
  description,
  isVisible,
  onValidationChange
}) => {
  const [validation, setValidation] = useState<PromptValidation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    console.log('PromptValidator useEffect triggered', {
      description: description.substring(0, 50),
      length: description.length,
      shouldValidate: description.trim().length >= 10
    });

    if (!description.trim() || description.length < 10) {
      console.log('PromptValidator: Description too short, clearing validation');
      setValidation(null);
      onValidationChange?.(null);
      return;
    }

    console.log('PromptValidator: Setting loading to true');
    setIsLoading(true);

    const validatePrompt = async () => {
      try {
        console.log(
          'Calling validatePromptQuality API with description:',
          description.substring(0, 100)
        );
        const result = await userPreferencesApi.validatePromptQuality(description);
        console.log('PromptValidator API result:', result);
        setValidation(result);
        onValidationChange?.(result);
      } catch (error) {
        console.error('Failed to validate prompt:', error);
        setValidation(null);
        onValidationChange?.(null);
      } finally {
        console.log('PromptValidator: Setting loading to false');
        setIsLoading(false);
      }
    };

    // Debounce validation
    console.log('PromptValidator: Setting up timer for validation');
    const timer = setTimeout(validatePrompt, 1500);
    return () => {
      console.log('PromptValidator: Clearing timer');
      clearTimeout(timer);
    };
  }, [description]);

  const getQualityIcon = (quality: string) => {
    switch (quality) {
      case 'excellent':
        return CheckCircle;
      case 'good':
        return CheckCircle;
      case 'needs_improvement':
        return AlertTriangle;
      default:
        return XCircle;
    }
  };

  const getQualityColor = (quality: string) => {
    switch (quality) {
      case 'excellent':
        return 'green';
      case 'good':
        return 'blue';
      case 'needs_improvement':
        return 'orange';
      default:
        return 'red';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'green.500';
    if (score >= 60) return 'blue.500';
    if (score >= 40) return 'orange.500';
    return 'red.500';
  };

  console.log('PromptValidator render', {
    isVisible,
    descriptionLength: description.length,
    isLoading,
    validation
  });

  if (!isVisible || !description.trim() || description.length < 10) {
    console.log('PromptValidator not rendering - visibility check failed');
    return null;
  }

  return (
    <Box mt={3}>
      {isLoading ? (
        <Box p={3} bg="gray.50" borderRadius="md" borderLeft="4px solid" borderColor="gray.300">
          <HStack spacing={2}>
            <Icon as={RefreshCw} color="gray.500" boxSize={4} className="animate-spin" />
            <Text fontSize="sm" color="gray.700">
              Analyzing prompt quality...
            </Text>
          </HStack>
        </Box>
      ) : validation ? (
        <Box
          p={3}
          bg={`${getQualityColor(validation.quality)}.50`}
          borderRadius="md"
          borderLeft="4px solid"
          borderColor={`${getQualityColor(validation.quality)}.400`}
        >
          <VStack spacing={3} align="stretch">
            {/* Quality header */}
            <HStack justify="space-between">
              <HStack spacing={2}>
                <Icon
                  as={getQualityIcon(validation.quality)}
                  color={`${getQualityColor(validation.quality)}.500`}
                  boxSize={4}
                />
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color={`${getQualityColor(validation.quality)}.800`}
                >
                  Prompt Quality:{' '}
                  {validation.quality.charAt(0).toUpperCase() +
                    validation.quality.slice(1).replace('_', ' ')}
                </Text>
                <Badge colorScheme={getQualityColor(validation.quality)} variant="subtle">
                  {validation.score}/100
                </Badge>
              </HStack>

              {(validation.issues.length > 0 || validation.suggestions.length > 0) && (
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme={getQualityColor(validation.quality)}
                  onClick={() => setShowDetails(!showDetails)}
                  rightIcon={showDetails ? <ChevronUp /> : <ChevronDown />}
                  _hover={{ bg: `${getQualityColor(validation.quality)}.100` }}
                  fontSize="xs"
                  fontWeight="medium"
                >
                  {showDetails
                    ? 'Less'
                    : validation.suggestions.length > 0
                      ? 'Suggestions'
                      : 'Details'}
                </Button>
              )}
            </HStack>

            {/* Progress bar */}
            <Box>
              <Progress
                value={validation.score}
                size="sm"
                colorScheme={getQualityColor(validation.quality)}
                borderRadius="full"
              />
              <HStack justify="space-between" mt={1}>
                <Text fontSize="xs" color="gray.500">
                  Poor
                </Text>
                <Text fontSize="xs" color="gray.600" fontWeight="medium">
                  Good
                </Text>
                <Text fontSize="xs" color="gray.500">
                  Excellent
                </Text>
              </HStack>
            </Box>

            {/* Quick reasoning */}
            <Text fontSize="xs" color={`${getQualityColor(validation.quality)}.700`}>
              {validation.reasoning}
            </Text>

            {/* Detailed feedback */}
            <Collapse in={showDetails} animateOpacity>
              <VStack spacing={2} align="stretch">
                {/* Issues */}
                {validation.issues.length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight="medium" color="orange.800" mb={1}>
                      ⚠️ Issues to Address:
                    </Text>
                    <VStack spacing={1} align="stretch">
                      {validation.issues.map((issue, idx) => (
                        <Text key={idx} fontSize="xs" color="orange.700">
                          • {issue}
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Suggestions */}
                {validation.suggestions.length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight="medium" color="blue.800" mb={1}>
                      💡 Suggestions:
                    </Text>
                    <VStack spacing={1} align="stretch">
                      {validation.suggestions.map((suggestion, idx) => (
                        <Text key={idx} fontSize="xs" color="blue.700">
                          • {suggestion}
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Recommendations */}
                <Box p={2} bg="white" borderRadius="sm" borderWidth={1} borderColor="gray.200">
                  <Text fontSize="xs" fontWeight="medium" color="gray.800" mb={1}>
                    Recommendations:
                  </Text>
                  <VStack spacing={1} align="stretch">
                    {validation.recommendations.isReady && (
                      <Text fontSize="xs" color="green.700">
                        ✅ Your prompt is ready to create an excellent custom feed
                      </Text>
                    )}
                    {validation.recommendations.shouldEnhance && (
                      <Text fontSize="xs" color="blue.700">
                        🔧 Consider using the "Enhance with AI" button for better results
                      </Text>
                    )}
                    {validation.recommendations.shouldRevise && (
                      <Text fontSize="xs" color="orange.700">
                        ✏️ Consider revising your prompt based on the suggestions above
                      </Text>
                    )}
                  </VStack>
                </Box>
              </VStack>
            </Collapse>
          </VStack>
        </Box>
      ) : (
        // Show a fallback when validation should be available but isn't loaded yet
        <Box p={3} bg="blue.50" borderRadius="md" borderLeft="4px solid" borderColor="blue.300">
          <Text fontSize="sm" color="blue.700">
            Prompt validation will appear here...
          </Text>
        </Box>
      )}
    </Box>
  );
};
